import React from 'react';
import { Helmet } from 'react-helmet-async';

const Terms: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Payment, Cancellation &amp; Online Terms | Sharon Clare Hypnotherapy</title>
        <meta
          name="description"
          content="Payment, cancellation, rescheduling and technical issue terms for hypnotherapy sessions with Sharon Clare Hypnotherapy."
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/terms/" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-[28px] font-heading font-bold text-gray-900 mb-4">
          Payment, Cancellation &amp; Online Terms
        </h1>
        <p className="text-gray-500 text-sm mb-8">Last updated: July 2026</p>

        <p className="text-gray-700 leading-relaxed text-base mb-8">
          These terms apply to all hypnotherapy sessions, in person and online. All times below are
          UK time (GMT/BST).
        </p>

        {/* Payment */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">Payment</h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Full payment is required at the time of booking.
        </p>

        {/* Cancellation */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">Cancellation</h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Cancellations must be made by WhatsApp or email. The cancellation policy is as follows:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>
            <strong>48+ hours before your appointment:</strong> full refund
          </li>
          <li>
            <strong>24–48 hours before:</strong> 50% refund
          </li>
          <li>
            <strong>Within 24 hours:</strong> no refund
          </li>
          <li>
            <strong>No-show</strong> (failure to attend with no notice): no refund
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Plans change, and the aim of this policy is to be fair to you while also respecting the
          time set aside for your session.
        </p>

        {/* Rescheduling */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          Rescheduling
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          You may reschedule your appointment free of charge with 12 or more hours&rsquo; notice.
          Requests to reschedule within 12 hours of your appointment will be treated as a late
          cancellation and are not eligible for a refund.
        </p>

        {/* Missed or late sessions */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          Missed or Late Sessions
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Arriving late by less than 15 minutes will result in a shortened session. Arriving 15
          minutes or more late means the session cannot proceed, and no refund will be given.
        </p>

        {/* Therapist cancellation */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          Therapist Cancellation
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          If Sharon Clare must cancel or change your session, you will be offered a new appointment
          or a full refund.
        </p>

        {/* IT issues */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          Technical Issues (Online Sessions)
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          If a disruption of 15 minutes or more occurs due to therapist-side technical issues, you
          will receive a full refund. If the disruption is on the client&rsquo;s side, no refund is
          given.
        </p>

        {/* Exceptions */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">Exceptions</h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          In cases of genuine emergency — for example sudden illness or bereavement — refund or
          rescheduling requests outside this policy will be considered at Sharon Clare&rsquo;s
          discretion on a case-by-case basis.
        </p>

        {/* Refunds */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">Refunds</h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Approved refunds are issued to the original payment method within 5–7 business days.
        </p>

        <p className="text-gray-700 leading-relaxed text-base mb-4 mt-10">
          See also our{' '}
          <a href="/privacy-policy" className="text-accent-600 hover:text-accent-700 underline">
            privacy, GDPR and consent information
          </a>
          .
        </p>
      </div>
    </>
  );
};

export default Terms;
